/**
 * Seller intake data contract constants (Sell Your Car M2).
 * @see docs/seller-intake-data-contract.md
 */

/** @readonly */
export const SELLER_TYPES = Object.freeze({
  INDIVIDUAL: "individual",
  DEALER: "dealer",
  SMALL_LOT: "small_lot",
  UNKNOWN: "unknown",
});

/** @readonly */
export const SELLER_PREFERRED_CONTACT_METHODS = Object.freeze({
  WHATSAPP: "whatsapp",
  PHONE: "phone",
  EMAIL: "email",
  ANY: "any",
});

/** @readonly */
export const SELLER_OWNERSHIP_STATUSES = Object.freeze({
  OWNER: "owner",
  DEALER_INVENTORY: "dealer_inventory",
  CONSIGNMENT: "consignment",
  UNKNOWN: "unknown",
});

/** Public/API result statuses — seller-facing only. */
export const SELLER_SUBMISSION_STATUSES = Object.freeze({
  RECEIVED: "received",
  VALIDATION_ERROR: "validation_error",
  DUPLICATE: "duplicate",
  SPAM_REJECTED: "spam_rejected",
  RATE_LIMITED: "rate_limited",
  SERVICE_ERROR: "service_error",
  NETWORK_ERROR: "network_error",
  UNKNOWN_ERROR: "unknown_error",
});

/** Internal admin review statuses — never expose on public pages. */
export const SELLER_ADMIN_REVIEW_STATUSES = Object.freeze({
  NEW: "new",
  REVIEWED: "reviewed",
  NEEDS_INFO: "needs_info",
  CONTACTED: "contacted",
  APPROVED: "approved",
  REJECTED: "rejected",
  CONVERTED_TO_LISTING: "converted_to_listing",
  ARCHIVED: "archived",
  SPAM: "spam",
});

/** Internal workflow statuses — admin/ops only. */
export const SELLER_WORKFLOW_STATUSES = Object.freeze({
  NOT_STARTED: "not_started",
  IN_REVIEW: "in_review",
  WAITING_ON_SELLER: "waiting_on_seller",
  READY_FOR_LISTING: "ready_for_listing",
  CONVERTED: "converted",
  CLOSED: "closed",
});

export const SELLER_SOURCE_PAGES = Object.freeze({
  SELL_YOUR_CAR: "sell_your_car",
});

export const SELLER_SOURCE_COMPONENTS = Object.freeze({
  SELL_YOUR_CAR_HERO_CTA: "sell_your_car_hero_cta",
  SELL_YOUR_CAR_FORM: "sell_your_car_form",
  SELL_YOUR_CAR_FINAL_CTA: "sell_your_car_final_cta",
  HOMEPAGE_SELL_CTA: "homepage_sell_cta",
  FOOTER_SELL_LINK: "footer_sell_link",
});

export const SELLER_SOURCE_ACTIONS = Object.freeze({
  SELLER_INTAKE_STARTED: "seller_intake_started",
  SELLER_SUBMISSION_ATTEMPTED: "seller_submission_attempted",
  SELLER_SUBMISSION_COMPLETED: "seller_submission_completed",
});

export const SELLER_DEFAULT_CURRENCY = "COP";

export const SELLER_PRIVACY_DEFAULTS = Object.freeze({
  requiresConsent: true,
  privacyPolicyHref: "/seguridad",
  privacyPolicyVersion: "automap-privacy-v1",
  allowAnalytics: true,
  piiBoundary: "seller_api_only",
});

/**
 * Product note (M2): askingPrice is optional at contract level.
 * Future validation may require it for individual sellers; dealers may omit for inventory batches.
 */
export const SELLER_FIELD_REQUIREMENTS = Object.freeze({
  sellerName: { required: true, futureValidation: true },
  sellerPhone: { required: true, futureValidation: true },
  sellerEmail: { required: false, requiredWhenPreferredContact: "email" },
  city: { required: true, futureValidation: true },
  department: { required: false },
  vehicleMake: { required: true, futureValidation: true },
  vehicleModel: { required: true, futureValidation: true },
  vehicleYear: { required: true, futureValidation: true },
  vehicleTrim: { required: false },
  vehicleMileageKm: { required: true, futureValidation: true },
  askingPrice: { required: false, recommended: true },
  conditionSummary: { required: false },
  ownershipStatus: { required: false },
  plate: { required: false, sensitive: true, adminOnly: true },
  vin: { required: false, sensitive: true, adminOnly: true },
  notes: { required: false, sensitive: true },
  consentAccepted: { required: true, futureValidation: true },
  marketingOptIn: { required: false, defaultFalse: true },
});

/** Spanish labels for future form UI (Milestone 3). */
export const SELLER_FIELD_LABELS_ES = Object.freeze({
  sellerType: "Tipo de vendedor",
  sellerName: "Nombre",
  sellerPhone: "Teléfono o WhatsApp",
  sellerEmail: "Correo electrónico",
  preferredContactMethod: "Método de contacto preferido",
  city: "Ciudad",
  department: "Departamento",
  vehicleMake: "Marca",
  vehicleModel: "Modelo",
  vehicleYear: "Año",
  vehicleTrim: "Versión o línea",
  vehicleMileageKm: "Kilometraje",
  askingPrice: "Precio esperado",
  currency: "Moneda",
  conditionSummary: "Estado general",
  ownershipStatus: "Relación con el vehículo",
  plate: "Placa",
  vin: "VIN",
  hasPhotos: "Fotos del vehículo",
  notes: "Notas adicionales",
  consentAccepted: "Acepto el uso de mis datos para revisar esta solicitud",
  marketingOptIn: "Quiero recibir novedades de AutoMapa",
});

export const SELLER_TYPE_LABELS_ES = Object.freeze({
  [SELLER_TYPES.INDIVIDUAL]: "Persona natural",
  [SELLER_TYPES.DEALER]: "Concesionario",
  [SELLER_TYPES.SMALL_LOT]: "Lote independiente",
  [SELLER_TYPES.UNKNOWN]: "No estoy seguro",
});

export const SELLER_OWNERSHIP_LABELS_ES = Object.freeze({
  [SELLER_OWNERSHIP_STATUSES.OWNER]: "Soy el dueño",
  [SELLER_OWNERSHIP_STATUSES.DEALER_INVENTORY]: "Inventario de concesionario",
  [SELLER_OWNERSHIP_STATUSES.CONSIGNMENT]: "Consignación",
  [SELLER_OWNERSHIP_STATUSES.UNKNOWN]: "Otro / no estoy seguro",
});

/** Seller-facing Spanish result messages — never echo PII. */
export const SELLER_RESULT_MESSAGES_ES = Object.freeze({
  [SELLER_SUBMISSION_STATUSES.RECEIVED]:
    "Recibimos la información de tu vehículo. Te contactaremos para revisar la publicación.",
  [SELLER_SUBMISSION_STATUSES.VALIDATION_ERROR]:
    "Revisa los datos del vehículo e intenta nuevamente.",
  [SELLER_SUBMISSION_STATUSES.DUPLICATE]:
    "Ya recibimos una solicitud similar. Si necesitas corregir algo, espera un momento e intenta nuevamente.",
  [SELLER_SUBMISSION_STATUSES.RATE_LIMITED]:
    "Has enviado varias solicitudes en poco tiempo. Intenta nuevamente más tarde.",
  [SELLER_SUBMISSION_STATUSES.SPAM_REJECTED]:
    "No pudimos procesar la solicitud. Intenta nuevamente.",
  [SELLER_SUBMISSION_STATUSES.SERVICE_ERROR]:
    "No pudimos enviar la información. Intenta nuevamente.",
  [SELLER_SUBMISSION_STATUSES.NETWORK_ERROR]:
    "No pudimos conectar. Revisa tu conexión e intenta nuevamente.",
  [SELLER_SUBMISSION_STATUSES.UNKNOWN_ERROR]:
    "No pudimos enviar la información. Intenta nuevamente.",
});

/** Future API route (not implemented in M2). */
export const SELLER_SUBMISSION_API_PATH = "/api/seller-submissions";

/** Future admin route (not implemented in M2). */
export const SELLER_SUBMISSION_ADMIN_PATH = "/admin/seller-submissions";
